import { useCallback, useMemo } from 'react';
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateFooter,
  EmptyStateTitle,
  Link,
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderIconTile,
  PageHeaderTitle,
} from '@customerio/pluma-components/react';
import { parseAsBoolean, parseAsString, useQueryState } from 'nuqs';
import { DocumentTitle } from '@/components/page/document-title';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import {
  AddCollectionButton,
  CollectionsTable,
  type CollectionsTableSorting,
  useCollectionFormModal,
  useCollectionWorkspaceLimits,
} from '@/features/collections';
import type { Collection } from '@/services/db/collection/entity';
import { useCollections } from '@/services/db/collection/queries';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { useAnalytics } from '@/services/analytics';

const DOCS_URL = 'https://docs.customer.io/journeys/collections/';
const DOCS_API_URL = 'https://docs.customer.io/journeys/collections/#collections-api';
const SORT_COLUMNS = ['name', 'rows', 'updated_at'] as const;

type CollectionsSortColumn = (typeof SORT_COLUMNS)[number];

function normalizeSortColumn(sort: string): CollectionsSortColumn {
  return SORT_COLUMNS.includes(sort as CollectionsSortColumn) ? (sort as CollectionsSortColumn) : 'name';
}

function compareCollectionValues(left: Collection, right: Collection, sort: CollectionsSortColumn): number {
  if (sort === 'rows') return left.rows - right.rows;
  if (sort === 'updated_at') return left.updated_at.getTime() - right.updated_at.getTime();
  return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
}

export default function CollectionsIndexPage() {
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const analytics = useAnalytics();
  // Sorting lives in the query string, so key the view on the pathname alone — otherwise
  // every column sort would emit another listViewed for the same visit.
  usePageViewTracking(() => void analytics.track('collections', 'listViewed', {}), true, { ignoreSearch: true });
  // One workspace-id source for the query and every mutation, so a workspace
  // transition can't show one workspace's collections while mutating another.
  const { data: collections } = useCollections({ workspaceId });
  const existingNames = collections.map((collection) => collection.name);
  const limits = useCollectionWorkspaceLimits(workspaceId);
  const collectionModal = useCollectionFormModal();
  const [sort, setSort] = useQueryState('sort', parseAsString.withDefault('name'));
  const [asc, setAsc] = useQueryState('asc', parseAsBoolean.withDefault(false));
  const sortColumn = normalizeSortColumn(sort);
  const sorting = useMemo<CollectionsTableSorting>(() => ({ id: sortColumn, desc: !asc }), [asc, sortColumn]);
  const sortedCollections = useMemo(
    () =>
      [...collections].sort((left, right) => {
        const comparison = compareCollectionValues(left, right, sortColumn);
        return asc ? comparison : -comparison;
      }),
    [asc, collections, sortColumn],
  );

  const retryInvalidImport = (collection: Collection) => {
    // This opens the same create modal as AddCollectionButton, so it emits the same event —
    // otherwise creationStarted under-reports the failed-import retry entry point.
    void analytics.track('collections', 'creationStarted', {});
    collectionModal.open({
      workspaceId,
      existingNames: existingNames.filter((name) => name !== collection.name),
      maxFileSizeBytes: limits.maxFileSizeBytes,
    });
  };
  const handleSortingChange = useCallback(
    (nextSorting: CollectionsTableSorting) => {
      void setSort(normalizeSortColumn(nextSorting.id));
      void setAsc(!nextSorting.desc);
    },
    [setAsc, setSort],
  );

  return (
    <>
      <DocumentTitle title="Collections" />
      <Page flexGrow="1">
        <PageHeader>
          <PageHeaderIconTile name="database" />
          <PageHeaderTitle>Collections</PageHeaderTitle>
          <PageHeaderDescription>
            Store data for things in your business, like classes or events, to use in your messages.{' '}
            <Link href={DOCS_URL} variant="secondary">
              Learn more about Collections
            </Link>
          </PageHeaderDescription>
          <PageHeaderActions>
            <AddCollectionButton
              workspaceId={workspaceId}
              existingNames={existingNames}
              maxCollectionCount={limits.maxCollectionCount}
              maxFileSizeBytes={limits.maxFileSizeBytes}
            />
          </PageHeaderActions>
        </PageHeader>
        <PageBody>
          {collections.length > 0 ? (
            <CollectionsTable
              collections={sortedCollections}
              maxFileSizeBytes={limits.maxFileSizeBytes}
              onInvalidImportRetry={retryInvalidImport}
              onSortingChange={handleSortingChange}
              sorting={sorting}
              workspaceId={workspaceId}
            />
          ) : (
            <EmptyState>
              <EmptyStateTitle icon="database">Use your business data in your messages</EmptyStateTitle>
              <EmptyStateDescription>
                Add a Collection for things in your business, like a list of upcoming events, promotions, or available
                classes to use in your messages.
              </EmptyStateDescription>
              <EmptyStateActions>
                <AddCollectionButton
                  workspaceId={workspaceId}
                  existingNames={existingNames}
                  maxCollectionCount={limits.maxCollectionCount}
                  maxFileSizeBytes={limits.maxFileSizeBytes}
                />
                <Button variant="secondary" href={DOCS_API_URL}>
                  View API Docs
                </Button>
              </EmptyStateActions>
              <EmptyStateFooter>
                <Link href={DOCS_URL}>Learn more about Collections</Link>
              </EmptyStateFooter>
            </EmptyState>
          )}
        </PageBody>
      </Page>
    </>
  );
}
